/**
 * Draft Letters' choices: the letter types the case's visa calls for (the
 * server's draft types, the same list New Draft offers) and, for each, the
 * firm's drafting templates that fit it, its marked default preselected.
 */
import type { FirmLibraryEntry } from "@/features/firmKnowledge/types";

import type { DraftTypeOption } from "../workspace/types";
import {
  defaultTemplatesFor,
  draftingGroups,
  sameVisa,
  type DefaultTemplate
} from "./defaultTemplates";
import type { LetterChoice, LetterType } from "./types";

/** Drafted from the packet or written freeform, so never a Draft Letters letter. */
const NOT_LETTERS: ReadonlySet<string> = new Set(["exhibit_list", "custom"]);

export const letterTypesOf = (options: DraftTypeOption[]): LetterType[] =>
  options
    .filter((o) => o.suggested && !NOT_LETTERS.has(o.draft_type))
    .map((o) => ({
      draftType: o.draft_type,
      label: o.label,
      checked: o.checked ?? true
    }));

export type TemplateOption = { entryId: string; name: string };

/**
 * The firm's templates for a letter type: `pinned` ones first (a custom case
 * type's own picks, whatever their visa), then this visa's, then any-visa ones.
 */
export const templateOptions = (
  library: readonly FirmLibraryEntry[],
  draftType: string,
  visa: string,
  pinned: readonly DefaultTemplate[] = []
): TemplateOption[] => {
  const seen = new Set<string>();
  const fresh = (o: TemplateOption) =>
    seen.has(o.entryId) ? false : (seen.add(o.entryId), true);
  const own = pinned
    .filter((d) => d.draftType === draftType)
    .map((d) => ({ entryId: d.entryId, name: d.name }));
  const fitting = draftingGroups(library)
    .filter(
      (g) =>
        g.draftType === draftType && (g.visa === null || sameVisa(g.visa, visa))
    )
    .sort((a, b) => (a.visa === null ? 1 : 0) - (b.visa === null ? 1 : 0))
    .flatMap((g) => g.entries)
    .map((e) => ({ entryId: e.id, name: e.name }));
  return [...own, ...fitting].filter(fresh);
};

/** A letter's pick: whether it's drafted and its template (null: CaseBlink's). */
export type LetterPick = { on: boolean; entryId: string | null };

export type LetterPicks = Partial<Record<string, LetterPick>>;

/**
 * Every letter type, checked as the server says, with the firm's default
 * template or CaseBlink's.
 */
export const defaultPicks = (
  types: LetterType[],
  library: readonly FirmLibraryEntry[],
  visa: string,
  templates: readonly DefaultTemplate[] = defaultTemplatesFor(library, visa)
): Record<string, LetterPick> => {
  const defaults = new Map(templates.map((d) => [d.draftType, d.entryId]));
  return Object.fromEntries(
    types.map((t) => [
      t.draftType,
      { on: t.checked ?? true, entryId: defaults.get(t.draftType) ?? null }
    ])
  );
};

/** The letters a run writes: the checked types, each with its template. */
export const lettersFor = (
  types: LetterType[],
  library: readonly FirmLibraryEntry[],
  visa: string,
  picks: LetterPicks = {},
  templates?: readonly DefaultTemplate[]
): LetterChoice[] => {
  const defaults = defaultPicks(types, library, visa, templates);
  const names = new Map(library.map((e) => [e.id, e.name]));
  return types.flatMap((t) => {
    const pick = picks[t.draftType] ?? defaults[t.draftType];
    if (!pick?.on) return [];
    const name = pick.entryId ? names.get(pick.entryId) : undefined;
    return [
      {
        draftType: t.draftType,
        label: t.label,
        template: pick.entryId && name ? { entryId: pick.entryId, name } : null
      }
    ];
  });
};
